import {
    ChatRequest,
    ChatResponse,
    ChartInfo,
    SessionUsage,
    SessionListResponse,
    SessionDetailResponse,
} from '../types/authService';
import { apiFetch } from './apiClient';
import { API_URL } from '@/lib/utils';

export const sendChatMessage = async (
    message: string,
    sessionId?: string,
    chartInfo?: ChartInfo | null
): Promise<ChatResponse> => {
    const requestBody: ChatRequest = { message };

    if (sessionId) {
        requestBody.session_id = sessionId;
    }

    if (chartInfo) {
        requestBody.chart_info = chartInfo;
    }

    const response = await apiFetch(`${API_URL}/ai/chat`, {
        method: 'POST',
        body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
        let detail = '';
        try {
            const body = await response.json();
            detail = typeof body?.detail === 'string' ? body.detail : '';
        } catch {
            // non-JSON body — fall through with empty detail
        }
        throw new Error(detail || `Failed to send chat message (HTTP ${response.status})`);
    }

    return response.json();
};

const emptySessionUsage = (sessionId: string): SessionUsage => ({
    session_id: sessionId,
    turns: 0,
    agent: { input_tokens: 0, output_tokens: 0, total_tokens: 0, cost_usd: 0 },
    router: { input_tokens: 0, output_tokens: 0, cost_usd: 0 },
    total_cost_usd: 0,
    by_intent: {},
});

export const fetchSessionUsage = async (sessionId: string): Promise<SessionUsage> => {
    const response = await apiFetch(
        `${API_URL}/ai/chat/sessions/${encodeURIComponent(sessionId)}/usage`,
        { method: 'GET' },
    );

    if (response.status === 404) {
        return emptySessionUsage(sessionId);
    }
    if (!response.ok) {
        throw new Error(`Failed to fetch session usage (HTTP ${response.status})`);
    }
    return response.json();
};

// List the current user's past chat sessions, newest-first (paginated).
export const listChatSessions = async (
    skip = 0,
    limit = 20,
): Promise<SessionListResponse> => {
    const response = await apiFetch(
        `${API_URL}/ai/chat/sessions?skip=${skip}&limit=${limit}`,
        { method: 'GET' },
    );

    if (!response.ok) {
        throw new Error(`Failed to list chat sessions (HTTP ${response.status})`);
    }
    return response.json();
};


export const fetchSessionDetail = async (
    sessionId: string,
): Promise<SessionDetailResponse> => {
    const response = await apiFetch(
        `${API_URL}/ai/chat/sessions/${encodeURIComponent(sessionId)}`,
        { method: 'GET' },
    );

    if (!response.ok) {
        throw new Error(`Failed to fetch session detail (HTTP ${response.status})`);
    }
    return response.json();
};
